import React, { useState } from "react";
import PropTypes from "prop-types";
import { getClassTypeLabel, getEnrollmentStatusLabel } from "./enrollmentDisplay";

const todayDate = () => new Date().toISOString().slice(0, 10);

const isPackageEnrollment = enrollment => (
  enrollment &&
  (
    enrollment.packageBalance ||
    ["awaitingPayment", "active", "expired", "depleted", "cancelled"].includes(enrollment.status)
  )
);

function calculatePaymentSummary(enrollment, clientLedger) {
  const terms = enrollment.termsSnapshot || enrollment.product || {};
  const plannedAmount = Number(terms.defaultPrice || 0);
  const paidAmount = (clientLedger || [])
    .filter(entry => entry.clientId === enrollment.clientId)
    .filter(entry => entry.type === "PAYMENT")
    .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);

  return {
    plannedAmount,
    paidAmount,
    missingAmount: Math.max(plannedAmount - paidAmount, 0),
  };
}

const RESERVATION_STATUS_LABELS = {
  reserved: "Zarezerwowana",
  consumed: "Wykorzystana",
  released: "Zwolniona",
};

const getStudentName = (studentId, students) => {
  const student = students.find(item => item.id === studentId);
  if (!student) return studentId;
  return `${student.firstName || ""} ${student.lastName || ""}`.trim() || studentId;
};

const getReservationStatusLabel = status => RESERVATION_STATUS_LABELS[status] || status || "-";

const getClassIdFromLessonId = (lessonId = "") => {
  const parts = lessonId.split("-");
  if (parts.length <= 1) return lessonId;
  return parts.slice(0, parts.length - 1).join("-");
};

const getReservationStartTime = (reservation, lessonRegister, schoolClasses) => {
  if (reservation.startTime) return reservation.startTime;
  const register = lessonRegister.find(item => (
    item &&
    (item.lessonId === reservation.lessonId || item.id === reservation.lessonId)
  ));
  if (register && register.lessonDetails && register.lessonDetails.startTime) {
    return register.lessonDetails.startTime;
  }
  const schoolClass = schoolClasses.find(item => item.id === getClassIdFromLessonId(reservation.lessonId));
  return schoolClass && schoolClass.startTime ? schoolClass.startTime : "";
};

const lessonIdentifier = (lessonId = "") => {
  if (!lessonId) return "-";
  if (lessonId.length <= 18) return lessonId;
  return `${lessonId.slice(0, 8)}...${lessonId.slice(-5)}`;
};

function PackageStatusPanel({
  enrollment,
  clientLedger,
  students,
  lessonRegister,
  schoolClasses,
  onActivate,
  onExtendValidity,
  disabled,
}) {
  const [activation, setActivation] = useState({ activationDate: todayDate(), note: "" });
  const [extension, setExtension] = useState({ validityEndDate: "", note: "" });

  if (!isPackageEnrollment(enrollment)) return null;

  const terms = enrollment.termsSnapshot || enrollment.product || {};
  const balance = enrollment.packageBalance || {};
  const canExtend = enrollment.status === "expired" && Number(balance.availableUnits || 0) > 0;
  const paymentSummary = calculatePaymentSummary(enrollment, clientLedger);

  const handleActivationChange = (event) => {
    const { name, value } = event.target;
    setActivation({ ...activation, [name]: value });
  };

  const handleExtensionChange = (event) => {
    const { name, value } = event.target;
    setExtension({ ...extension, [name]: value });
  };

  return (
    <section className="mt-3 mb-3">
      <h3 className="manage-title h5">{terms.name || "Pakiet"}</h3>
      <p className="manage-text mb-1">
        <span>Status pakietu: </span>
        <strong>{getEnrollmentStatusLabel(enrollment.status)}</strong>
      </p>
      {enrollment.paymentDueDate && (
        <p className="manage-text mb-1">{`Termin platnosci: ${enrollment.paymentDueDate}`}</p>
      )}
      {enrollment.validityEndDate && (
        <p className="manage-text mb-1">{`Wazny do: ${enrollment.validityEndDate}`}</p>
      )}
      {enrollment.packageBalance && (
        <div className="manage-text mb-2">
          <span className="mr-3">{`Razem: ${balance.totalUnits}`}</span>
          <span className="mr-3">{`Zuzyte: ${balance.consumedUnits}`}</span>
          <span className="mr-3">{`Zarezerwowane: ${balance.reservedUnits}`}</span>
          <span>{`Dostepne: ${balance.availableUnits}`}</span>
        </div>
      )}
      {(enrollment.packageReservations || []).length > 0 && (
        <section className="mt-3">
          <h3 className="manage-title h5">Lekcje w pakiecie</h3>
          <div className="row table-header manage-table-title text-center mx-0 d-flex align-items-center">
            <div className="col-sm-2 table-cell">Data</div>
            <div className="col-sm-1 table-cell">Godzina</div>
            <div className="col-sm-2 table-cell">Uczen</div>
            <div className="col-sm-3 table-cell">Typ lekcji</div>
            <div className="col-sm-2 table-cell">Status</div>
            <div className="col-sm-2 table-cell">Lekcja</div>
          </div>
          {[...(enrollment.packageReservations || [])]
            .sort((left, right) => (
              `${left.lessonDate || ""}#${getReservationStartTime(left, lessonRegister, schoolClasses)}#${left.lessonId || ""}`
                .localeCompare(`${right.lessonDate || ""}#${getReservationStartTime(right, lessonRegister, schoolClasses)}#${right.lessonId || ""}`)
            ))
            .map((reservation, index) => {
              const startTime = getReservationStartTime(reservation, lessonRegister, schoolClasses);
              return (
                <div
                  key={reservation.id || `${reservation.lessonId}#${reservation.studentId}`}
                  className={`table-row row d-flex manage-text text-center mx-0 ${index % 2 !== 0 ? "table-row-alternate" : ""}`}
                >
                  <div className="col-sm-2 table-cell">{reservation.lessonDate || "-"}</div>
                  <div className="col-sm-1 table-cell">{startTime || "-"}</div>
                  <div className="col-sm-2 table-cell">{getStudentName(reservation.studentId, students)}</div>
                  <div className="col-sm-3 table-cell">{getClassTypeLabel(reservation.classTypeId)}</div>
                  <div className="col-sm-2 table-cell">{getReservationStatusLabel(reservation.status)}</div>
                  <div className="col-sm-2 table-cell" title={reservation.lessonId || ""}>{lessonIdentifier(reservation.lessonId)}</div>
                </div>
              );
            })}
        </section>
      )}

      {enrollment.status === "awaitingPayment" && (
        <section className="mt-3">
          <h3 className="manage-title h5">Aktywacja pakietu</h3>
          <p className="manage-text mb-2">
            Operator moze aktywowac pakiet recznie. Akcja zapisze decyzje w historii zapisu.
          </p>
          {paymentSummary.missingAmount > 0 && (
            <div className="alert alert-warning">
              {`Brakuje zarejestrowanych wplat: ${paymentSummary.missingAmount}. Sprawdz rozliczenia klienta przed aktywacja pakietu.`}
            </div>
          )}
          <div className="form-row">
          <div className="form-group col-md-3">
            <label htmlFor="package-activation-date">Data aktywacji</label>
            <input
              id="package-activation-date"
              className="form-control"
              type="date"
              name="activationDate"
              value={activation.activationDate}
              onChange={handleActivationChange}
            />
          </div>
          <div className="form-group col-md-7">
            <label htmlFor="package-activation-note">Notatka do aktywacji</label>
            <input
              id="package-activation-note"
              className="form-control"
              name="note"
              value={activation.note}
              onChange={handleActivationChange}
            />
          </div>
          <div className="form-group col-md-2 d-flex align-items-end">
            <button
              type="button"
              className="btn manage-button manage-red-button btn-block"
              disabled={disabled}
              onClick={() => onActivate({ ...activation, paymentSummary })}
            >
              Aktywuj pakiet
            </button>
          </div>
          </div>
        </section>
      )}

      {canExtend && (
        <div className="form-row">
          <div className="form-group col-md-4">
            <label htmlFor="package-validity-end-date">Nowa data waznosci</label>
            <input
              id="package-validity-end-date"
              className="form-control"
              type="date"
              name="validityEndDate"
              value={extension.validityEndDate}
              onChange={handleExtensionChange}
            />
          </div>
          <div className="form-group col-md-5">
            <label htmlFor="package-extension-note">Notatka do przedluzenia</label>
            <input
              id="package-extension-note"
              className="form-control"
              name="note"
              value={extension.note}
              onChange={handleExtensionChange}
            />
          </div>
          <div className="form-group col-md-3 d-flex align-items-end">
            <button
              type="button"
              className="btn manage-button manage-red-button btn-block"
              disabled={disabled}
              onClick={() => onExtendValidity(extension)}
            >
              Przedluz waznosc
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

PackageStatusPanel.propTypes = {
  enrollment: PropTypes.object.isRequired,
  clientLedger: PropTypes.array,
  students: PropTypes.array,
  lessonRegister: PropTypes.array,
  schoolClasses: PropTypes.array,
  onActivate: PropTypes.func.isRequired,
  onExtendValidity: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

PackageStatusPanel.defaultProps = {
  clientLedger: [],
  students: [],
  lessonRegister: [],
  schoolClasses: [],
  disabled: false,
};

export default PackageStatusPanel;
